/**
 * Exit Intent Popup
 * Detects when a user is about to leave the page and shows a popup
 * with "You may also like" product recommendations
 */

if (!customElements.get('exit-intent-popup')) {
  customElements.define(
    'exit-intent-popup',
    class ExitIntentPopup extends HTMLElement {
      constructor() {
        super();
        this.hasShown = false;
        this.popupShownKey = 'exitIntentPopupShown';
        this.popupShownCountKey = 'exitIntentPopupShownCount';
        this.popupShownHourKey = 'exitIntentPopupShownHour';
        this.popupDelayKey = 'exitIntentPopupDelay';
        this.maxShowsPerHour = 3;
        this.hourInMs = 60 * 60 * 1000; // 1 hour in milliseconds
        this.minTimeOnPage = 3000; // Minimum 3 seconds on page before showing
        this.mobileTimerDelay = 30000; // 30 seconds for mobile timer
        this.mobileScrollThreshold = 50; // Pixels scrolled up quickly to trigger
        
        // Detect if mobile device
        this.isMobile = this.detectMobile();
        
        // Mobile-specific tracking
        this.mobileTimerStarted = false;
        this.mobileTimerId = null;
        this.lastScrollTop = 0;
        this.scrollUpDetected = false;
        
        // Check for test mode in URL or localStorage
        const urlParams = new URLSearchParams(window.location.search);
        this.testMode = urlParams.get('exitIntentTest') === '1' || localStorage.getItem('exitIntentTestMode') === 'true';
        
        if (this.testMode) {
          // In test mode, bypass cooldown and time restrictions
          this.maxShowsPerHour = Infinity;
          this.minTimeOnPage = 0;
          this.mobileTimerDelay = 0;
          console.log('[Exit Intent] 🧪 TEST MODE ENABLED - Cooldown and time restrictions bypassed');
          console.log('[Exit Intent] To disable test mode, remove ?exitIntentTest=1 from URL or run: localStorage.removeItem("exitIntentTestMode")');
        }
        
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.initialize());
        } else {
          this.initialize();
        }
      }
      
      /**
       * Detect if user is on a mobile device
       */
      detectMobile() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
               (window.innerWidth <= 768 && 'ontouchstart' in window);
      }

      /**
       * Check if we're on a product page
       */
      isProductPage() {
        // Check multiple ways to detect product pages
        const checks = {
          bodyClass: document.body?.classList.contains('template-product'),
          urlPath: window.location.pathname.includes('/products/'),
          hasProductForm: !!document.querySelector('product-form'),
          hasProductRecommendations: !!document.querySelector('product-recommendations'),
          hasMainProduct: !!document.querySelector('[id^="MainProduct-"]'),
          hasProductJson: !!document.querySelector('[id^="ProductJSON-"]'),
          hasVariantPicker: !!document.querySelector('variant-radios, variant-selects, variant-picker')
        };

        const isProduct = Object.values(checks).some(check => check === true);

        if (!isProduct) {
          console.log('[Exit Intent] Not on product page. Checks:', checks);
        } else {
          console.log('[Exit Intent] Product page detected. Checks:', checks);
        }

        return isProduct;
      }

      /**
       * Initialize the exit intent popup
       */
      initialize() {
        try {
          // Only show on product pages - use robust detection
          if (!this.isProductPage()) {
            return;
          }

          // Always set up exit intent detection, but check blocks when exit intent is detected
          // This allows the popup to show once the minimum time requirement is met
          console.log('[Exit Intent] Initializing exit intent detection...');
          this.init();
          
          // Log initial block status if any
          const initialBlockCheck = this.shouldBlockPopup();
          if (initialBlockCheck) {
            // Don't return - let listeners be set up, they'll check again when exit intent is detected
            console.log('[Exit Intent] Initial checks show popup would be blocked, but listeners are active. Will re-check when exit intent is detected.');
          } else {
            console.log('[Exit Intent] Exit intent detection active - ready to show popup');
          }
        } catch (error) {
          console.error('[Exit Intent] Initialization error:', error);
        }
      }

      /**
       * Check if popup should be blocked due to recent display or insufficient time on page
       */
      shouldBlockPopup() {
        try {
          const now = Date.now();
          const currentHour = Math.floor(now / this.hourInMs);
          
          // Get stored hour and count
          const storedHour = parseInt(localStorage.getItem(this.popupShownHourKey) || '0', 10);
          let shownCount = parseInt(localStorage.getItem(this.popupShownCountKey) || '0', 10);
          
          // Reset count if we're in a new hour
          if (currentHour !== storedHour) {
            shownCount = 0;
            localStorage.setItem(this.popupShownCountKey, '0');
            localStorage.setItem(this.popupShownHourKey, currentHour.toString());
            console.log('[Exit Intent] New hour detected, resetting show count');
          }
          
          // Check if we've exceeded the max shows per hour
          if (shownCount >= this.maxShowsPerHour) {
            const minutesUntilNextHour = 60 - (new Date().getMinutes());
            console.log(`[Exit Intent] Blocked: Already shown ${shownCount} times this hour (max: ${this.maxShowsPerHour}). Resets in ${minutesUntilNextHour} minutes`);
            return true;
          }

          // Check minimum time on page
          const pageLoadTime = performance.timing?.navigationStart || Date.now();
          const timeOnPage = now - pageLoadTime;
          const secondsOnPage = (timeOnPage / 1000).toFixed(1);
          
          if (timeOnPage < this.minTimeOnPage) {
            // Store page load time if not already stored
            if (!localStorage.getItem(this.popupDelayKey)) {
              localStorage.setItem(this.popupDelayKey, pageLoadTime.toString());
            }
            const secondsRemaining = ((this.minTimeOnPage - timeOnPage) / 1000).toFixed(1);
            console.log(`[Exit Intent] Blocked by minimum time: Only ${secondsOnPage}s on page, need ${this.minTimeOnPage/1000}s (${secondsRemaining}s remaining)`);
            return true;
          }

          console.log(`[Exit Intent] No blocks detected. Time on page: ${secondsOnPage}s, Shows this hour: ${shownCount}/${this.maxShowsPerHour}`);
          return false;
        } catch (error) {
          console.warn('[Exit Intent] Error checking should block:', error);
          // If localStorage fails, allow popup but be conservative
          return true;
        }
      }

      /**
       * Initialize exit intent detection
       */
      init() {
        if (this.isMobile) {
          // Mobile: Set up scroll detection and timer
          this.initMobile();
        } else {
          // Desktop: Set up mouse exit intent detection
          this.initDesktop();
        }
        
        // Track page load time
        const pageLoadTime = performance.timing?.navigationStart || Date.now();
        if (!localStorage.getItem(this.popupDelayKey)) {
          localStorage.setItem(this.popupDelayKey, pageLoadTime.toString());
        }
        
        // In test mode, expose a function to manually trigger the popup
        if (this.testMode) {
          window.exitIntentTest = () => {
            console.log('[Exit Intent] 🧪 TEST MODE: Manual trigger via exitIntentTest()');
            this.hasShown = false;
            this.handleExitIntent();
          };
          console.log('[Exit Intent] 🧪 TEST MODE: Type exitIntentTest() in console to manually trigger popup');
        }
      }
      
      /**
       * Initialize desktop exit intent detection
       */
      initDesktop() {
        // Track mouse movement to detect exit intent
        let mouseOutBoundary = false;
        let mouseY = 0;
        let exitIntentTriggered = false;

        // Detect mouse moving toward top of viewport (exit intent)
        const handleMouseMove = (e) => {
          if (exitIntentTriggered || this.hasShown) return;
          
          mouseY = e.clientY;

          // If mouse is in the top 5% of viewport (near browser address bar), consider it exit intent
          if (mouseY < window.innerHeight * 0.05 && !mouseOutBoundary) {
            mouseOutBoundary = true;
            exitIntentTriggered = true;
            console.log('[Exit Intent] Mouse detected in top 5% of viewport - exit intent triggered');
            this.handleExitIntent();
          } else if (mouseY > window.innerHeight * 0.05) {
            // Reset boundary flag if mouse moves back down
            mouseOutBoundary = false;
          }
        };

        document.addEventListener('mousemove', handleMouseMove);

        // Also detect mouse leaving the document (most reliable exit intent)
        const handleMouseLeave = (e) => {
          if (e.clientY <= 0 && !this.hasShown && !exitIntentTriggered) {
            exitIntentTriggered = true;
            console.log('[Exit Intent] Mouse left document (y <= 0) - exit intent triggered');
            this.handleExitIntent();
          }
        };

        document.addEventListener('mouseleave', handleMouseLeave);
        console.log('[Exit Intent] Desktop: Mouse event listeners attached (mousemove, mouseleave)');
      }
      
      /**
       * Initialize mobile exit intent detection (scroll up or timer)
       */
      initMobile() {
        console.log('[Exit Intent] Mobile: Initializing scroll detection and timer');
        
        // Start the 30-second timer
        this.startMobileTimer();
        
        // Set up scroll detection
        this.initMobileScroll();
      }
      
      /**
       * Start the 30-second timer for mobile
       */
      startMobileTimer() {
        if (this.mobileTimerStarted || this.hasShown) return;
        
        this.mobileTimerStarted = true;
        const pageLoadTime = performance.timing?.navigationStart || Date.now();
        const timeOnPage = Date.now() - pageLoadTime;
        
        // Timer should trigger after 30 seconds total, but respect minimum 3 seconds
        // Calculate when the timer should fire (30 seconds from page load)
        const timerTargetTime = pageLoadTime + this.mobileTimerDelay;
        const now = Date.now();
        let delay = timerTargetTime - now;
        
        // Ensure we wait at least until minimum time on page
        const minTimeTarget = pageLoadTime + this.minTimeOnPage;
        const minTimeDelay = minTimeTarget - now;
        
        // Use the later of the two (30 seconds or minimum time)
        delay = Math.max(delay, minTimeDelay);
        
        if (delay <= 0) {
          // Already past the delay, trigger immediately if conditions are met
          setTimeout(() => {
            if (!this.hasShown && !this.scrollUpDetected) {
              console.log('[Exit Intent] Mobile: 30-second timer triggered');
              this.handleExitIntent();
            }
          }, 100);
        } else {
          this.mobileTimerId = setTimeout(() => {
            if (!this.hasShown && !this.scrollUpDetected) {
              console.log('[Exit Intent] Mobile: 30-second timer triggered');
              this.handleExitIntent();
            }
          }, delay);
          console.log(`[Exit Intent] Mobile: Timer started, will trigger in ${(delay / 1000).toFixed(1)}s`);
        }
      }
      
      /**
       * Initialize mobile scroll detection
       */
      initMobileScroll() {
        let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        let lastScrollTime = Date.now();
        let scrollVelocity = 0;
        
        const handleScroll = () => {
          if (this.hasShown || this.scrollUpDetected) return;
          
          const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const currentTime = Date.now();
          const scrollDelta = lastScrollTop - currentScrollTop; // Positive when scrolling up
          const timeDelta = currentTime - lastScrollTime;
          
          // Calculate scroll velocity (pixels per second)
          if (timeDelta > 0) {
            scrollVelocity = Math.abs(scrollDelta / (timeDelta / 1000));
          }
          
          // Detect quick scroll up (scrolling up at more than threshold pixels per second)
          if (scrollDelta > 0 && scrollVelocity > this.mobileScrollThreshold) {
            this.scrollUpDetected = true;
            // Clear the timer since scroll up triggered first
            if (this.mobileTimerId) {
              clearTimeout(this.mobileTimerId);
              this.mobileTimerId = null;
            }
            console.log(`[Exit Intent] Mobile: Quick scroll up detected (velocity: ${scrollVelocity.toFixed(1)} px/s)`);
            this.handleExitIntent();
            return;
          }
          
          lastScrollTop = currentScrollTop;
          lastScrollTime = currentTime;
        };
        
        // Use passive listener for better performance
        window.addEventListener('scroll', handleScroll, { passive: true });
        console.log('[Exit Intent] Mobile: Scroll detection active');
      }

      /**
       * Check if a bundle iframe (e.g. the Revy bundle builder) is currently
       * open and visible on the screen. When it is, we suppress the popup so it
       * doesn't cover the bundle experience.
       */
      isBundleIframeOpen() {
        try {
          // Match iframes belonging to the bundle app, either by their source
          // URL (Revy) or by a bundle-related id/class.
          const iframes = document.querySelectorAll('iframe');
          for (const iframe of iframes) {
            const src = (iframe.getAttribute('src') || '').toLowerCase();
            const id = (iframe.id || '').toLowerCase();
            const className = (iframe.className || '').toLowerCase();

            const looksLikeBundle =
              src.includes('revy.io') ||
              src.includes('bundle') ||
              id.includes('bundle') ||
              className.includes('bundle');

            if (looksLikeBundle && this.isElementVisible(iframe)) {
              console.log('[Exit Intent] Bundle iframe is open and visible - suppressing popup');
              return true;
            }
          }
          return false;
        } catch (error) {
          console.warn('[Exit Intent] Error checking for bundle iframe:', error);
          return false;
        }
      }

      /**
       * Determine whether an element is actually visible on screen.
       */
      isElementVisible(el) {
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return false;

        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
          return false;
        }

        // offsetParent is null for display:none ancestors; allow fixed-position
        // elements which legitimately have a null offsetParent.
        if (el.offsetParent === null && style.position !== 'fixed') return false;

        return true;
      }

      /**
       * Handle exit intent detection
       */
      handleExitIntent() {
        try {
          // Never show the popup while a bundle iframe is open on the screen,
          // even in test mode.
          if (this.isBundleIframeOpen()) {
            console.log('[Exit Intent] Exit intent detected but a bundle iframe is open - not showing popup');
            return;
          }

          // In test mode, allow multiple triggers
          if (!this.testMode) {
            if (this.hasShown || this.shouldBlockPopup()) {
              console.log('[Exit Intent] Exit intent detected but popup blocked (already shown or limit reached)');
              return;
            }
            this.hasShown = true;
            
            // Update show count for hourly limit
            const now = Date.now();
            const currentHour = Math.floor(now / this.hourInMs);
            const storedHour = parseInt(localStorage.getItem(this.popupShownHourKey) || '0', 10);
            let shownCount = parseInt(localStorage.getItem(this.popupShownCountKey) || '0', 10);
            
            // Reset count if we're in a new hour
            if (currentHour !== storedHour) {
              shownCount = 0;
            }
            
            // Increment count
            shownCount++;
            localStorage.setItem(this.popupShownCountKey, shownCount.toString());
            localStorage.setItem(this.popupShownHourKey, currentHour.toString());
            localStorage.setItem(this.popupShownKey, now.toString());
            
            console.log(`[Exit Intent] Show count updated: ${shownCount}/${this.maxShowsPerHour} this hour`);
          } else {
            // Test mode: reset hasShown and skip blocking
            this.hasShown = false;
            console.log('[Exit Intent] 🧪 TEST MODE: Exit intent detected, bypassing all blocks');
          }

          console.log('[Exit Intent] Exit intent detected! Preparing to show popup...');

          // Clear mobile timer if it's still running
          if (this.mobileTimerId) {
            clearTimeout(this.mobileTimerId);
            this.mobileTimerId = null;
          }

          // Show the popup
          this.show();
        } catch (error) {
          console.error('[Exit Intent] Error handling exit intent:', error);
        }
      }

      /**
       * Show the exit intent popup
       */
      show() {
        try {
          // Try multiple selectors to find modal (it may have been moved to body)
          let modal = document.getElementById('ExitIntentModal');
          if (!modal) {
            modal = document.querySelector('exit-intent-popup modal-dialog');
          }
          if (!modal) {
            modal = document.querySelector('modal-dialog.exit-intent-modal-dialog');
          }

          if (!modal) {
            console.error('[Exit Intent] Modal not found! Make sure "Exit Intent Popup" section is added to the product template in Theme Customizer.');
            return;
          }

          console.log('[Exit Intent] Modal found, getting product ID...');

          // Get current product ID
          const productId = this.getProductId();
          if (!productId) {
            console.warn('[Exit Intent] Could not determine product ID. Popup will not show recommendations.');
            // Still show modal but with empty recommendations
            if (modal && typeof modal.show === 'function') {
              modal.show();
              console.log('[Exit Intent] Modal shown (without recommendations)');
            }
            return;
          }

          console.log(`[Exit Intent] Product ID found: ${productId}. Loading recommendations...`);

          // Show modal immediately with loading state, then update with recommendations
          if (modal && typeof modal.show === 'function') {
            modal.show();
            console.log('[Exit Intent] Modal shown (loading recommendations...)');
          }

          // Load product recommendations with timeout
          const loadPromise = this.loadRecommendations(productId);
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Recommendations load timeout after 10 seconds')), 10000);
          });

          Promise.race([loadPromise, timeoutPromise])
            .then(() => {
              console.log('[Exit Intent] ✅ Recommendations loaded successfully!');
            })
            .catch((error) => {
              console.error('[Exit Intent] Error loading recommendations:', error);
              // Update container to show error/empty state
              let recommendationsContainer = this.querySelector('.exit-intent-recommendations') ||
                                           document.getElementById('ExitIntentModal')?.querySelector('.exit-intent-recommendations') ||
                                           document.querySelector('.exit-intent-recommendations');
              if (recommendationsContainer) {
                recommendationsContainer.innerHTML = '<p class="exit-intent-empty">Unable to load recommendations at this time. Please try again later.</p>';
              }
            });
        } catch (error) {
          console.error('[Exit Intent] Error showing popup:', error);
        }
      }

      /**
       * Get current product ID from the page
       */
      getProductId() {
        // Try to get from product form
        const productForm = document.querySelector('product-form form');
        if (productForm) {
          const productIdInput = productForm.querySelector('input[name="id"]');
          if (productIdInput && productIdInput.value) {
            console.log('[Exit Intent] Product ID found from product form:', productIdInput.value);
            return productIdInput.value;
          }
        }

        // Try to get from product recommendations element
        const recommendations = document.querySelector('product-recommendations[data-url]');
        if (recommendations && recommendations.dataset.url) {
          const urlMatch = recommendations.dataset.url.match(/product_id=(\d+)/);
          if (urlMatch && urlMatch[1]) {
            console.log('[Exit Intent] Product ID found from recommendations element:', urlMatch[1]);
            return urlMatch[1];
          }
        }

        // Try meta tag
        const metaProductId = document.querySelector('meta[property="product:id"]');
        if (metaProductId && metaProductId.content) {
          console.log('[Exit Intent] Product ID found from meta tag:', metaProductId.content);
          return metaProductId.content;
        }

        console.warn('[Exit Intent] Could not find product ID. Tried: product form, recommendations element, meta tag');
        return null;
      }

      /**
       * Load product recommendations for the popup
       */
      async loadRecommendations(productId) {
        // Find the recommendations container - it might be in the modal which could be moved to body
        let recommendationsContainer = this.querySelector('.exit-intent-recommendations');
        
        // If not found in exit-intent-popup, try to find it in the modal (which might be moved to body)
        if (!recommendationsContainer) {
          const modal = document.getElementById('ExitIntentModal') || 
                       document.querySelector('modal-dialog.exit-intent-modal-dialog');
          if (modal) {
            recommendationsContainer = modal.querySelector('.exit-intent-recommendations');
            console.log('[Exit Intent] Found container in modal element');
          }
        }
        
        // If still not found, try searching in document body (modal might have been moved)
        if (!recommendationsContainer) {
          recommendationsContainer = document.querySelector('.exit-intent-recommendations');
          console.log('[Exit Intent] Found container via document query');
        }
        
        if (!recommendationsContainer) {
          console.error('[Exit Intent] Recommendations container not found in DOM');
          console.error('[Exit Intent] Debug info:', {
            hasExitIntentPopup: !!this,
            exitIntentPopupHTML: this?.outerHTML?.substring(0, 200),
            modalInBody: !!document.body.querySelector('#ExitIntentModal'),
            modalInPopup: !!this.querySelector('#ExitIntentModal'),
            allContainers: document.querySelectorAll('.exit-intent-recommendations').length
          });
          return;
        }
        
        console.log('[Exit Intent] Recommendations container found:', recommendationsContainer);

        // Check if products are already loaded from metafields (Liquid rendered them)
        const existingProducts = recommendationsContainer.querySelector('.product-grid');
        if (existingProducts && existingProducts.querySelectorAll('.grid__item').length > 0) {
          console.log('[Exit Intent] ✅ Products already loaded from metafields via Liquid');
          return; // Products are already there, no need to fetch
        }

        // Clear any existing product-recommendations elements that might be hidden
        const hiddenLoader = recommendationsContainer.querySelector('product-recommendations');
        if (hiddenLoader) {
          hiddenLoader.remove();
          console.log('[Exit Intent] Removed hidden product-recommendations loader');
        }

        // Show loading state if not already populated
        if (!recommendationsContainer.querySelector('.product-grid')) {
          recommendationsContainer.innerHTML = '<div class="exit-intent-loading"><p>Loading recommendations...</p></div>';
          console.log('[Exit Intent] Showing loading state...');
        }

        try {
          const limit = parseInt(recommendationsContainer?.dataset?.productsLimit || 4, 10);
          const rootUrl = window.Shopify?.routes?.root || '/';
          
          // Strategy 1: Try to use the same recommendations API as "You may also like" section
          // Look for existing product-recommendations element on the page
          const existingRecommendations = document.querySelector('product-recommendations[data-url]');
          let recommendationsUrl = null;
          
          if (existingRecommendations && existingRecommendations.dataset.url) {
            recommendationsUrl = existingRecommendations.dataset.url;
            console.log('[Exit Intent] Found recommendations URL from existing product-recommendations element:', recommendationsUrl);
          } else {
            // Strategy 2: Construct the URL using the same pattern as related-products section
            // Find the related-products section to get its section ID
            const relatedProductsSection = document.querySelector('.related-products, [class*="related-products"]')?.closest('section[data-section-id]');
            if (relatedProductsSection) {
              const sectionId = relatedProductsSection.dataset.sectionId;
              recommendationsUrl = `${rootUrl}recommendations/products?section_id=${sectionId}&product_id=${productId}&limit=${limit}`;
              console.log('[Exit Intent] Constructed recommendations URL from related-products section:', recommendationsUrl);
            }
          }
          
          // Strategy 3: Try metafield if recommendations API URL is not available
          if (!recommendationsUrl) {
            const productHandles = recommendationsContainer?.dataset?.relatedProductHandles;
            if (productHandles && productHandles.trim() !== '') {
              console.log('[Exit Intent] Using metafield product handles as fallback');
              const handles = productHandles.split(',').filter(h => h.trim()).slice(0, limit);
              
              // Fetch each product by handle using the Product API
              const productPromises = handles.map(async (handle) => {
                const productUrl = `${rootUrl}products/${handle.trim()}.js`;
                try {
                  const response = await fetch(productUrl);
                  if (!response.ok) return null;
                  return await response.json();
                } catch (error) {
                  console.error(`[Exit Intent] Error fetching product ${handle}:`, error);
                  return null;
                }
              });
              
              const products = (await Promise.all(productPromises)).filter(p => p !== null);
              if (products.length > 0) {
                console.log('[Exit Intent] ⚠️ Metafield fallback: Products found but manual card building not implemented');
                console.log('[Exit Intent] ⚠️ Please use recommendations API instead (should work with Strategy 1 or 2)');
                // For now, show a message - in production, you could build cards here
                recommendationsContainer.innerHTML = '<p class="exit-intent-empty">Products found in metafield, but please configure recommendations API.</p>';
                return;
              }
            }
            
            // If we get here, no products found
            console.warn('[Exit Intent] ⚠️ No recommendations URL or metafield products found');
            recommendationsContainer.innerHTML = '<p class="exit-intent-empty">No related products available at this time.</p>';
            return;
          }
          
          // Fetch recommendations using the same API as "You may also like"
          console.log('[Exit Intent] Fetching recommendations from API:', recommendationsUrl);
          const response = await fetch(recommendationsUrl);
          
          if (!response.ok) {
            console.error(`[Exit Intent] Failed to fetch recommendations: ${response.status} ${response.statusText}`);
            recommendationsContainer.innerHTML = '<p class="exit-intent-empty">Unable to load recommendations at this time.</p>';
            return;
          }
          
          const html = await response.text();
          console.log('[Exit Intent] ✅ Received recommendations HTML');
          
          // Parse the HTML response
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          
          // Extract the product grid from the response
          const productGrid = doc.querySelector('.product-grid, ul.grid, product-recommendations .product-grid');
          
          if (productGrid && productGrid.querySelectorAll('.grid__item').length > 0) {
            // Clone the grid and limit to the requested number
            const grid = productGrid.cloneNode(true);
            const items = Array.from(grid.querySelectorAll('.grid__item')).slice(0, limit);
            
            // Create a new grid with only the limited items
            const newGrid = document.createElement('ul');
            newGrid.className = grid.className;
            newGrid.setAttribute('role', 'list');
            items.forEach(item => newGrid.appendChild(item));
            
            recommendationsContainer.innerHTML = '';
            recommendationsContainer.appendChild(newGrid);
            
            // Re-initialize components for dynamically loaded products
            this.initializeComponents();
            
            // Setup quick add buttons for dynamically loaded products
            setTimeout(() => {
              this.setupQuickAddButtons();
            }, 100);
            
            console.log(`[Exit Intent] ✅ Successfully loaded ${items.length} recommendations from API`);
          } else {
            console.warn('[Exit Intent] ⚠️ No product grid found in recommendations response');
            recommendationsContainer.innerHTML = '<p class="exit-intent-empty">No recommendations available at this time.</p>';
          }
          
        } catch (error) {
          console.error('[Exit Intent] ❌ Error loading recommendations:', error);
          console.error('[Exit Intent] Error details:', {
            message: error.message,
            stack: error.stack,
            productId: productId
          });
          
          // Show user-friendly error message
          if (recommendationsContainer) {
            recommendationsContainer.innerHTML = '<p class="exit-intent-error">Unable to load recommendations. Please try refreshing the page.</p>';
          }
          
          // Re-throw so the caller can handle it
          throw error;
        }
      }

      /**
       * Initialize components after loading recommendations
       */
      initializeComponents() {
        try {
          // Initialize any lazy-loaded images
          const images = this.querySelectorAll('img[loading="lazy"]');
          if (images.length > 0) {
            console.log(`[Exit Intent] Converting ${images.length} lazy images to eager loading`);
            images.forEach((img) => {
              img.loading = 'eager';
            });
          }

          // Prevent link navigation when clicking quick add button
          this.setupQuickAddButtons();

          // Trigger any custom events that might be needed
          this.dispatchEvent(new CustomEvent('exit-intent:recommendations-loaded', { bubbles: true }));
          console.log('[Exit Intent] Custom event dispatched: exit-intent:recommendations-loaded');
        } catch (error) {
          console.warn('[Exit Intent] Warning during component initialization:', error);
        }
      }

      /**
       * Setup quick add buttons to prevent link navigation
       */
      setupQuickAddButtons() {
        const quickAddButtons = document.querySelectorAll('.exit-intent-quick-add-button');
        quickAddButtons.forEach((button) => {
          button.addEventListener('click', (e) => {
            // Prevent the link from being triggered
            e.stopPropagation();
            e.preventDefault();
            
            // Find the form and submit it
            const form = button.closest('.exit-intent-quick-add-form');
            if (form) {
              // Add loading state
              button.classList.add('loading');
              button.disabled = true;
              
              // Submit the form
              form.requestSubmit();
            }
          });
        });

        // Also prevent overlay clicks from triggering the link
        const overlays = document.querySelectorAll('.exit-intent-quick-add-overlay');
        overlays.forEach((overlay) => {
          overlay.addEventListener('click', (e) => {
            // Only prevent if clicking the overlay itself, not the button
            if (e.target === overlay) {
              e.stopPropagation();
            }
          });
        });
      }
    }
  );
}

